// core-preference-integration-serializer.class.ts
import { Serializer } from "../resource/serializer.interface";
import { 
    CorePreferenceIntegration, 
    ComplianceInputMapping, 
    ConsentCodeMapping, 
    ChannelMapping 
} from "./core-preference-integration.model"

export class CorePreferenceIntegrationSerializer implements Serializer {
    fromJson(json: any): CorePreferenceIntegration {
        const integration = new CorePreferenceIntegration();
        
        // Map basic properties
        integration.id = json.id;
        integration.corePreferenceId = json.corePreferenceId;
        integration.integrationType = json.integrationType;
        integration.status = json.status;
        integration.description = json.description;
        integration.createOnTrue = json.createOnTrue === undefined ? true : json.createOnTrue;
        integration.createOnFalse = json.createOnFalse === undefined ? false : json.createOnFalse;
        integration.instanceId = json.instanceId;
        
        // Map default mapping
        if (json.defaultMapping) {
            const defaultMapping = new ComplianceInputMapping();
            defaultMapping.processingPurpose = json.defaultMapping.processingPurpose;
            defaultMapping.permissionType = json.defaultMapping.permissionType;
            defaultMapping.businessUnit = json.defaultMapping.businessUnit;
            
            // Map consent mapping
            if (json.defaultMapping.consentMapping) {
                const consentMapping = new ConsentCodeMapping();
                consentMapping.trueValue = json.defaultMapping.consentMapping.trueValue || "yes";
                consentMapping.falseValue = json.defaultMapping.consentMapping.falseValue || "no";
                defaultMapping.consentMapping = consentMapping;
            } else {
                defaultMapping.consentMapping = new ConsentCodeMapping();
            }
            
            integration.defaultMapping = defaultMapping;
        } else {
            // Set default values if missing
            integration.defaultMapping = new ComplianceInputMapping();
        }
        
        // Map channel mappings
        if (json.channelMappings && Array.isArray(json.channelMappings)) {
            integration.channelMappings = json.channelMappings.map((cm: any) => {
                const channelMapping = new ChannelMapping();
                channelMapping.channelId = cm.channelId;
                
                if (cm.mapping) {
                    const mapping = new ComplianceInputMapping();
                    mapping.processingPurpose = cm.mapping.processingPurpose;
                    mapping.permissionType = cm.mapping.permissionType;
                    mapping.businessUnit = cm.mapping.businessUnit;
                    
                    if (cm.mapping.consentMapping) {
                        const consentMapping = new ConsentCodeMapping();
                        consentMapping.trueValue = cm.mapping.consentMapping.trueValue || "yes";
                        consentMapping.falseValue = cm.mapping.consentMapping.falseValue || "no";
                        mapping.consentMapping = consentMapping;
                    } else {
                        mapping.consentMapping = new ConsentCodeMapping();
                    }
                    
                    channelMapping.mapping = mapping;
                } else {
                    channelMapping.mapping = new ComplianceInputMapping();
                }
                
                return channelMapping;
            });
        } else {
            integration.channelMappings = [];
        }
        
        return integration;
    }

    toJson(integration: CorePreferenceIntegration): any {
        // Create a clean object to send to the server
        return {
            id: integration.id,
            corePreferenceId: integration.corePreferenceId,
            integrationType: integration.integrationType,
            defaultMapping: integration.defaultMapping ? {
                processingPurpose: integration.defaultMapping.processingPurpose,
                permissionType: integration.defaultMapping.permissionType,
                businessUnit: integration.defaultMapping.businessUnit,
                consentMapping: integration.defaultMapping.consentMapping ? {
                    trueValue: integration.defaultMapping.consentMapping.trueValue,
                    falseValue: integration.defaultMapping.consentMapping.falseValue
                } : undefined
            } : undefined,
            channelMappings: integration.channelMappings ? integration.channelMappings.map((cm: { channelId: any; mapping: { processingPurpose: any; permissionType: any; businessUnit: any; consentMapping: { trueValue: any; falseValue: any; }; }; }) => ({
                channelId: cm.channelId,
                mapping: cm.mapping ? {
                    processingPurpose: cm.mapping.processingPurpose,
                    permissionType: cm.mapping.permissionType,
                    businessUnit: cm.mapping.businessUnit,
                    consentMapping: cm.mapping.consentMapping ? {
                        trueValue: cm.mapping.consentMapping.trueValue,
                        falseValue: cm.mapping.consentMapping.falseValue
                    } : undefined
                } : undefined
            })) : [],
            status: integration.status,
            description: integration.description,
            createOnTrue: integration.createOnTrue,
            createOnFalse: integration.createOnFalse,
            instanceId: integration.instanceId
        };
    }
}